import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useReports, ReportFilters, SearchQuery } from '../hooks/useReports';
import { useAuth } from '../hooks/useAuth';
import { useFirebase } from '../lib/firebase/FirebaseProvider';
import { useDebugMode } from '../hooks/useDebugMode';
import { Report } from '../hooks/useReports';
import MapView from '../components/MapView';
import TreasureCard from '../components/TreasureCard';
import AdvancedSearchFilter from '../components/AdvancedSearchFilter';
import MobileSearchInterface from '../components/MobileSearchInterface';
import DebugPanel from '../components/DebugPanel';
import { SkeletonGrid } from '../components/LoadingStates';
import { EmptyState } from '../components/EmptyStates';
import { ErrorState } from '../components/ErrorStates';
import { GridLoadMore, CompactLoadMore } from '../components/LoadMorePagination';
import { TreasureCardAd } from '../components/ads/TreasureCardAd';
import { FeatureGate } from '../components/FeatureGate';
import { shouldShowAd, getAdPosition } from '../utils/adPlacement';
import ShareModal from '../components/ShareModal';
import '../components/MapView.css';

const TreasuresPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { user } = useFirebase();
  const { isAdmin, role, customClaims } = useAuth();
  const isDebugMode = useDebugMode();
  const {
    reports,
    isLoading,
    error,
    hasMore,
    searchMetadata,
    facets,
    fetchReports,
    searchReports,
    fetchReportsWithFacets,
    loadMore,
    clearError
  } = useReports();

  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [hoveredReport, setHoveredReport] = useState<Report | null>(null);
  const [currentFilters, setCurrentFilters] = useState<ReportFilters>({});
  const [currentSearchQuery, setCurrentSearchQuery] = useState<SearchQuery | null>(null);
  const [favoriteReportIds, setFavoriteReportIds] = useState<Set<string>>(new Set());
  const [mapCenter, setMapCenter] = useState<[number, number] | null>(null);
  const [mapZoom, setMapZoom] = useState<number>(12);
  const [isMapBoundsChanging, setIsMapBoundsChanging] = useState(false);
  const [mapBounds, setMapBounds] = useState<{ north: number; south: number; east: number; west: number } | null>(null);
  const [visibleReports, setVisibleReports] = useState<Report[]>([]);
  const [isInitialMapLoad, setIsInitialMapLoad] = useState(true);
  const [boundsFilteringEnabled, setBoundsFilteringEnabled] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareReport, setShareReport] = useState<Report | null>(null);

  // Refs for scroll synchronization
  const treasureListRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Initialize with URL parameters and load reports
  useEffect(() => {
    let mounted = true;

    const loadReports = async () => {
      if (mounted) {
        // Parse URL parameters
        const text = searchParams.get('text');
        const postcode = searchParams.get('postcode');
        const suburb = searchParams.get('suburb');
        const tags = searchParams.get('tags');
        const categories = searchParams.get('categories');
        const dateFrom = searchParams.get('dateFrom');

        if (text) {
          // If there's a text search, use searchReports
          const searchQuery: SearchQuery = {
            text: text.trim(),
            status: undefined, // Let backend handle default status
            postcode: postcode || undefined,
            tags: tags ? tags.split(',') : undefined,
            categories: categories ? categories.split(',') : undefined,
            dateFrom: dateFrom ? new Date(dateFrom) : undefined,
            sortBy: 'relevance',
            sortOrder: 'desc',
            limit: 20
          };
          setCurrentSearchQuery(searchQuery);
          setCurrentFilters({});
          await searchReports(searchQuery);
        } else {
          // Regular filtering
          const filters: ReportFilters = {};
          
          if (postcode) filters.postcode = postcode;
          if (suburb) filters.suburb = suburb;
          if (tags) filters.tags = tags.split(',');
          if (categories) filters.categories = categories.split(',');
          if (dateFrom) filters.dateFrom = new Date(dateFrom);

          // Set default filters for unified view
          filters.sortBy = 'submittedAt';
          filters.sortOrder = 'desc';
          filters.limit = 20; // Load more for map view

          setCurrentFilters(filters);
          setCurrentSearchQuery(null);
          await fetchReports(filters);
        }
      }
    };

    loadReports();

    return () => {
      mounted = false;
    };
  }, [fetchReports, searchParams]);

  // Handle initial map load completion
  useEffect(() => {
    if (reports.length > 0 && isInitialMapLoad) {
      // Give the map time to fit bounds to all reports
      const timer = setTimeout(() => {
        // Debug: Initial map load completed
        setIsInitialMapLoad(false);
        setBoundsFilteringEnabled(true);
        // Initialize visible reports to all reports
        setVisibleReports(reports);
      }, 1000); // 1 second delay to allow map to settle

      return () => clearTimeout(timer);
    }
  }, [reports, isInitialMapLoad]);

  // Handle search queries
  const handleSearch = useCallback(async (searchQuery: SearchQuery) => {
    setCurrentSearchQuery(searchQuery);
    setCurrentFilters({});
    await searchReports(searchQuery);
  }, [searchReports]);

  // Handle filter application
  const handleFilter = useCallback(async (filters: ReportFilters) => {
    setCurrentFilters(filters);
    setCurrentSearchQuery(null);
    await fetchReports(filters);
  }, [fetchReports]);

  // Handle faceted search
  const handleFacetedSearch = useCallback(async (filters: Omit<ReportFilters, 'startAfter' | 'limit'>) => {
    setCurrentFilters(filters);
    setCurrentSearchQuery(null);
    await fetchReportsWithFacets(filters);
  }, [fetchReportsWithFacets]);

  // Handle load more
  const handleLoadMore = useCallback(async () => {
    if (currentSearchQuery) {
      await loadMore(undefined, currentSearchQuery);
    } else {
      await loadMore(currentFilters);
    }
  }, [loadMore, currentFilters, currentSearchQuery]);

  // Handle report selection from map or list
  const handleReportSelect = useCallback((report: Report | null) => {
    setSelectedReport(report);
    
    // If selecting a report, center map on it and scroll to card
    if (report && report.location) {
      setMapCenter([report.location.latitude, report.location.longitude]);
      setMapZoom(15);
      
      // Scroll to corresponding card
      const cardElement = cardRefs.current.get(report.id);
      if (cardElement && treasureListRef.current) {
        cardElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }
  }, []);

  // Handle map center change (when user pans the map)
  const handleMapMove = useCallback((center: [number, number], zoom: number) => {
    setMapCenter(center);
    setMapZoom(zoom);
  }, []);

  // Handle map bounds change to filter visible reports
  const handleMapBoundsChange = useCallback((bounds: { north: number; south: number; east: number; west: number }, _isUserInitiated: boolean = false) => {
    // Debug: Map bounds changed
    
    setMapBounds(bounds);
    
    // Only apply bounds filtering if:
    // 1. It's not the initial map load, AND
    // 2. Bounds filtering is enabled (after user interaction), AND
    // 3. We have reports to filter
    if (!isInitialMapLoad && boundsFilteringEnabled && reports.length > 0) {
      setIsMapBoundsChanging(true);
      
      // Filter reports that are within the visible map bounds
      const reportsInBounds = reports.filter(report => {
        if (!report.location) return false;
        
        const { latitude, longitude } = report.location;
        return (
          latitude >= bounds.south &&
          latitude <= bounds.north &&
          longitude >= bounds.west &&
          longitude <= bounds.east
        );
      });
      
      // Debug: Filtered reports
      setVisibleReports(reportsInBounds);
      
      // Clear the loading state after a short delay
      setTimeout(() => {
        setIsMapBoundsChanging(false);
      }, 300);
    } else {
      // During initial load or when filtering is disabled, show all reports
      setVisibleReports(reports);
      // Debug: Showing all reports
    }
  }, [reports, isInitialMapLoad, boundsFilteringEnabled]);

  // Handle treasure card hover - highlight corresponding map marker
  const handleCardHover = useCallback((report: Report | null) => {
    setHoveredReport(report);
    
    // If hovering over a card, ensure the corresponding marker is visible
    if (report && report.location && boundsFilteringEnabled) {
      // Optionally center the map on the hovered report if it's not visible
      if (mapBounds) {
        const { latitude, longitude } = report.location;
        const isVisible = (
          latitude >= mapBounds.south &&
          latitude <= mapBounds.north &&
          longitude >= mapBounds.west &&
          longitude <= mapBounds.east
        );
        
        if (!isVisible) {
          // Debug: Centering map on hovered report
          setMapCenter([latitude, longitude]);
        }
      }
    }
  }, [mapBounds, boundsFilteringEnabled]);

  // Handle favorite functionality
  const handleFavorite = useCallback((reportId: string, isFavorited: boolean) => {
    setFavoriteReportIds(prev => {
      const newSet = new Set(prev);
      if (isFavorited) {
        newSet.add(reportId);
      } else {
        newSet.delete(reportId);
      }
      return newSet;
    });

    // TODO: Implement actual favorite functionality with backend
    console.log(`${isFavorited ? 'Added' : 'Removed'} favorite for report ${reportId}`);
  }, []);

  // Handle share functionality
  const handleShare = useCallback((reportId: string) => {
    const report = reports.find(r => r.id === reportId);
    if (report) {
      setShareReport(report);
      setShareModalOpen(true);
    }
  }, [reports]);

  const handleCloseShareModal = useCallback(() => {
    setShareModalOpen(false);
    setShareReport(null);
  }, []);

  return (
    <div className="h-screen flex flex-col bg-sky-neutral">
      {/* Header with search and filters */}
      <div className="bg-white border-b border-urban/10 p-4 z-10 flex-shrink-0">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-4">
            {/* Title */}
            <div className="flex items-center justify-between">
              <h1 className="text-2xl sm:text-3xl font-bold text-urban">Treasures</h1>
              
              {/* Results count */}
              <div className="text-sm text-urban/70">
                {boundsFilteringEnabled && mapBounds && visibleReports.length !== reports.length ? (
                  <>
                    {visibleReports.length} of {reports.length} treasure{reports.length !== 1 ? 's' : ''} in view
                  </>
                ) : (
                  <>
                    {reports.length} treasure{reports.length !== 1 ? 's' : ''}
                  </>
                )}
                {isMapBoundsChanging && (
                  <span className="ml-2 text-cardboard">
                    <span className="animate-spin inline-block">⟳</span> Updating...
                  </span>
                )}
                {isInitialMapLoad && reports.length > 0 && (
                  <span className="ml-2 text-verge">
                    <span className="animate-pulse">📍</span> Positioning map...
                  </span>
                )}
              </div>
            </div>

            {/* Search and Filters - Responsive */}
            <div className="flex-1">
              <div className="block md:hidden">
                <MobileSearchInterface
                  onSearch={handleSearch}
                  onFilter={handleFilter}
                  onFacetedSearch={handleFacetedSearch}
                  facets={facets || undefined}
                  searchMetadata={searchMetadata || undefined}
                  isLoading={isLoading}
                />
              </div>
              <div className="hidden md:block">
                <AdvancedSearchFilter
                  onSearch={handleSearch}
                  onFilter={handleFilter}
                  onFacetedSearch={handleFacetedSearch}
                  facets={facets || undefined}
                  searchMetadata={searchMetadata || undefined}
                  isLoading={isLoading}
                  isAdmin={isAdmin}
                />
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <ErrorState
              title="Error Loading Treasures"
              message={error}
              onRetry={() => {
                clearError();
                if (currentSearchQuery) {
                  searchReports(currentSearchQuery);
                } else {
                  fetchReports(currentFilters);
                }
              }}
              onDismiss={clearError}
              variant="inline"
              className="mt-4"
            />
          )}
        </div>
      </div>

      {/* Main content area - Airbnb-inspired split layout */}
      <div className="flex-1 overflow-hidden">
        {/* Desktop: Side-by-side layout */}
        <div className="hidden lg:flex h-full">
          {/* Left side: Treasure cards */}
          <div className="w-1/2 bg-white border-r border-urban/10 flex flex-col">
            <div 
              ref={treasureListRef}
              className={`flex-1 overflow-y-auto p-6 relative ${
                isMapBoundsChanging ? 'treasures-list-updating' : ''
              }`}
            >
              {/* Loading skeletons */}
              {isLoading && reports.length === 0 && (
                <SkeletonGrid count={6} type="card" />
              )}
              
              {/* Treasure cards grid with ads */}
              {!isLoading && reports.length > 0 && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  {(boundsFilteringEnabled && mapBounds ? visibleReports : reports).map((report, index) => (
                    <React.Fragment key={report.id}>
                      <div
                        ref={(el) => {
                          if (el) {
                            cardRefs.current.set(report.id, el);
                          } else {
                            cardRefs.current.delete(report.id);
                          }
                        }}
                        className={`transition-all duration-200 ${
                          selectedReport?.id === report.id 
                            ? 'ring-2 ring-cardboard ring-offset-2 ring-offset-white' 
                            : ''
                        } ${
                          hoveredReport?.id === report.id 
                            ? 'treasure-card-highlighted' 
                            : ''
                        }`}
                        onMouseEnter={() => handleCardHover(report)}
                        onMouseLeave={() => handleCardHover(null)}
                        onClick={() => handleReportSelect(report)}
                      >
                        <TreasureCard
                          report={report}
                          onFavorite={handleFavorite}
                          isFavorited={favoriteReportIds.has(report.id)}
                          showCoins={true}
                          onShare={handleShare}
                        />
                      </div>
                      
                      {/* Insert ad after every 4th treasure card */}
                      {shouldShowAd(index) && (
                        <FeatureGate flag="GOOGLE_ADS_ENABLED" key={`ad-${getAdPosition(index)}`}>
                          <TreasureCardAd 
                            position={getAdPosition(index)}
                            className="treasure-card-ad-grid-item"
                          />
                        </FeatureGate>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
              
              {/* Empty state */}
              {!isLoading && reports.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <EmptyState
                    icon="🔍"
                    title="No treasures found"
                    description="Try adjusting your filters or check back later for new treasures."
                  />
                </div>
              )}
              
              {/* No treasures in current map view */}
              {!isLoading && reports.length > 0 && boundsFilteringEnabled && mapBounds && visibleReports.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <EmptyState
                      icon="🗺️"
                      title="No treasures in this area"
                      description="Pan or zoom the map to see treasures in other areas."
                    />
                    <button
                      onClick={() => {
                        // Debug: Showing all treasures (desktop)
                        setBoundsFilteringEnabled(false);
                        setVisibleReports(reports);
                        setMapBounds(null);
                      }}
                      className="mt-4 px-4 py-2 bg-cardboard text-white rounded-lg hover:bg-cardboard-600 transition-colors"
                    >
                      Show All Treasures
                    </button>
                  </div>
                </div>
              )}

              {/* Enhanced Load More with Infinite Scroll */}
              <GridLoadMore
                hasMore={hasMore}
                isLoading={isLoading}
                onLoadMore={handleLoadMore}
                currentCount={boundsFilteringEnabled && mapBounds ? visibleReports.length : reports.length}
                infiniteScrollEnabled={true}
                loadMoreText="Discover More Treasures"
                completionMessage="You've explored all treasures in this area! Try adjusting your filters or search to discover more."
                className="mt-8"
              />
            </div>
          </div>

          {/* Right side: Map */}
          <div className="w-1/2 relative">
            <MapView
              reports={reports}
              selectedReport={selectedReport || hoveredReport}
              onReportSelect={handleReportSelect}
              center={mapCenter}
              zoom={mapZoom}
              onMapMove={handleMapMove}
              onMapBoundsChange={handleMapBoundsChange}
              isLoading={isLoading}
              hoveredReport={hoveredReport}
            />
          </div>
        </div>

        {/* Mobile/Tablet: Stacked layout */}
        <div className="lg:hidden flex flex-col h-full">
          {/* Top: Map */}
          <div className="h-1/2 relative">
            <MapView
              reports={reports}
              selectedReport={selectedReport}
              onReportSelect={handleReportSelect}
              center={mapCenter}
              zoom={mapZoom}
              onMapMove={handleMapMove}
              onMapBoundsChange={handleMapBoundsChange}
              isLoading={isLoading}
              hoveredReport={hoveredReport}
            />
          </div>

          {/* Bottom: Treasure cards */}
          <div className="h-1/2 bg-white border-t border-urban/10 flex flex-col">
            <div 
              ref={treasureListRef}
              className={`flex-1 overflow-y-auto p-4 relative ${
                isMapBoundsChanging ? 'treasures-list-updating' : ''
              }`}
            >
              {/* Loading skeletons */}
              {isLoading && reports.length === 0 && (
                <SkeletonGrid count={4} type="card" />
              )}
              
              {/* Treasure cards grid with ads */}
              {!isLoading && reports.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(boundsFilteringEnabled && mapBounds ? visibleReports : reports).map((report, index) => (
                    <React.Fragment key={report.id}>
                      <div
                        ref={(el) => {
                          if (el) {
                            cardRefs.current.set(report.id, el);
                          } else {
                            cardRefs.current.delete(report.id);
                        }
                        }}
                        className={`transition-all duration-200 ${
                          selectedReport?.id === report.id 
                            ? 'ring-2 ring-cardboard ring-offset-2 ring-offset-white' 
                            : ''
                        }`}
                        onClick={() => handleReportSelect(report)}
                      >
                        <TreasureCard
                          report={report}
                          onFavorite={handleFavorite}
                          isFavorited={favoriteReportIds.has(report.id)}
                          showCoins={true}
                          onShare={handleShare}
                        />
                      </div>
                      
                      {/* Insert ad after every 4th treasure card */}
                      {shouldShowAd(index) && (
                        <FeatureGate flag="GOOGLE_ADS_ENABLED" key={`ad-${getAdPosition(index)}`}>
                          <TreasureCardAd 
                            position={getAdPosition(index)}
                            className="treasure-card-ad-grid-item"
                          />
                        </FeatureGate>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
              
              {/* Empty state */}
              {!isLoading && reports.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <EmptyState
                    icon="🔍"
                    title="No treasures found"
                    description="Try adjusting your filters or check back later for new treasures."
                  />
                </div>
              )}
              
              {/* No treasures in current map view */}
              {!isLoading && reports.length > 0 && boundsFilteringEnabled && mapBounds && visibleReports.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <EmptyState
                      icon="🗺️"
                      title="No treasures in this area"
                      description="Pan or zoom the map to see treasures in other areas."
                    />
                    <button
                      onClick={() => {
                        // Debug: Showing all treasures (mobile)
                        setBoundsFilteringEnabled(false);
                        setVisibleReports(reports);
                        setMapBounds(null);
                      }}
                      className="mt-4 px-4 py-2 bg-cardboard text-white rounded-lg hover:bg-cardboard-600 transition-colors"
                    >
                      Show All Treasures
                    </button>
                  </div>
                </div>
              )}

              {/* Enhanced Load More for Mobile */}
              <CompactLoadMore
                hasMore={hasMore}
                isLoading={isLoading}
                onLoadMore={handleLoadMore}
                currentCount={boundsFilteringEnabled && mapBounds ? visibleReports.length : reports.length}
                infiniteScrollEnabled={true}
                loadMoreText="Load More Treasures"
                completionMessage="All treasures loaded"
                className="mt-6"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Debug Info - Only shown when ?debug=true */}
      {isDebugMode && (
        <div className="fixed bottom-4 left-4 right-4 lg:right-auto lg:w-96 bg-sky-neutral border border-urban-200 rounded-lg p-4 z-[1003] max-h-64 overflow-y-auto">
          <h3 className="text-lg font-semibold mb-2 text-urban">Debug Info</h3>
          <div className="text-sm text-urban-300 space-y-1">
            <div>User: {user ? user.email : 'Not logged in'}</div>
            <div>Role: {role || 'visitor'}</div>
            <div>Claims: {JSON.stringify(customClaims)}</div>
            <div>Reports loaded: {reports.length}</div>
            <div>Selected: {selectedReport?.id || 'None'}</div>
            <div>Hovered: {hoveredReport?.id || 'None'}</div>
            <div>Loading: {isLoading ? 'Yes' : 'No'}</div>
            <div>Error: {error || 'None'}</div>
            <div>Map Center: {mapCenter ? `${mapCenter[0].toFixed(4)}, ${mapCenter[1].toFixed(4)}` : 'Auto'}</div>
            <div>Map Zoom: {mapZoom}</div>
            <div>Visible Reports: {boundsFilteringEnabled && mapBounds ? visibleReports.length : 'All'}</div>
            <div>Bounds Changing: {isMapBoundsChanging ? 'Yes' : 'No'}</div>
            <div>Initial Load: {isInitialMapLoad ? 'Yes' : 'No'}</div>
            <div>Bounds Filtering: {boundsFilteringEnabled ? 'Enabled' : 'Disabled'}</div>
          </div>
        </div>
      )}

      {/* Debug Panel - Only shown to moderators/admins */}
      {isAdmin && <DebugPanel />}

      {/* Share Modal */}
      {shareReport && (
        <ShareModal
          isOpen={shareModalOpen}
          onClose={handleCloseShareModal}
          reportId={shareReport.id}
          reportTitle={shareReport.tags?.join(', ') || 'Curbside Treasure'}
          reportImage={shareReport.images?.[0]?.url}
          reportLocation={`${shareReport.location.suburb}, ${shareReport.location.postcode}`}
        />
      )}
    </div>
  );
};

export default TreasuresPage;